/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Switch,
  useTheme,
  alpha
} from '@mui/material';
import { useParams, useHistory } from 'react-router';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import NewTyres from 'src/components/tyres/NewTyres';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { getTyreDetails, updateTyreDetails } from 'src/redux/tyreSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import useAuth from 'src/context/AuthContext';
import ROUTE from 'src/utils/routeConstants';
import { getFormattedDate, hasAdminLikeAccess } from 'src/utils/getInitials';

function TyreDetails() {
  const theme = useTheme();
  const { tyreId } = useParams();
  const history = useHistory();
  const { getUser } = useAuth();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const dispatch = useDispatch();
  const { loading, tyreDetails } = useSelector((state) => state.tyres);

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getTyreDetails(tyreId)).unwrap();
  }, [dispatch, tyreId]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      const values = { ...tyreDetails, active: !tyreDetails.active };
      await dispatch(updateTyreDetails(values)).unwrap();
      dispatch(setSuccessInfo({ successMsg: 'Tyre status updated successfully!' }));
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewTyres
        open={openModal}
        initialValues={tyreDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)',
        }}
      >
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  display: 'flex'
                }}
              >
                <TireRepairIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  TYRE DETAILS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage tyre configuration
                </Typography>
              </Box>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.tyreManangement)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Back
            </Button>
          )}
        />

        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {Object.entries(tyreDetails || {})
              .filter(([key, value]) => {
                if (key === 'id') return false;
                if (typeof value === 'object' && value !== null && !value.label) return false;
                return true;
              })
              .map(([key, value]) => {
                if (key === 'active') {
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            STATUS
                          </Typography>
                          <Typography
                            fontWeight={600}
                            color={value ? 'success.main' : 'error.main'}
                          >
                            {value ? 'Active' : 'Inactive'}
                          </Typography>
                        </Box>

                        <Switch
                          checked={value}
                          onChange={() => hasAdminAccess && setShowConfirmModal(true)}
                          disabled={!hasAdminAccess}
                          color="success"
                        />
                      </Box>
                    </Grid>
                  );
                }

                const label = key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (char) => char.toUpperCase());

                let displayValue = value;

                if (key.includes('date') && value) {
                  displayValue = getFormattedDate(value);
                }

                if (typeof value === 'object' && value?.label) {
                  displayValue = value.label;
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 0.5
                        }}
                      >
                        {label}
                      </Typography>

                      <Typography variant="body1" fontWeight={600}>
                        {displayValue ?? '--'}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </CardContent>

        {hasAdminAccess && (
          <>
            <Divider />
            <CardActions sx={{
              p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
            }}
            >
              <Button
                variant="contained"
                onClick={() => setOpenModal(true)}
                startIcon={<EditIcon />}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1
                }}
              >
                Edit Tyre Details
              </Button>
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        info={
          tyreDetails?.active
            ? CONFIRMATION_MSG.disableSite
            : CONFIRMATION_MSG.enableSite
        }
        open={showConfirmModal}
        disable={tyreDetails?.active}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default TyreDetails;
